/**
 * voiceCommands — hands-free control words for the guided voice tour.
 *
 * The tour is meant to be driven without touching the keyboard, so a handful of
 * spoken words act as navigation instead of answers.
 *
 * Matching is deliberately strict: the WHOLE utterance must be one of these
 * words. The guided flow collects free-text names, addresses and consultant
 * names, so a substring match would quietly swallow real answers — an address
 * containing "back" would navigate instead of being recorded. "next please"
 * is likewise treated as an answer, not a command.
 *
 * English only for now. The eight dictation locales in ./languages.js could get
 * their own command words later; the existing spoken prompts are still awaiting
 * professional translation, so guessing at command vocabulary would be worse
 * than the on-screen buttons the operator already has.
 */

/** command name → the exact utterances that trigger it */
const COMMANDS = {
  accept: ['next', 'accept'],
  redo: ['redo', 'retry'],
  skip: ['skip'],
  back: ['back'],
  exit: ['exit', 'stop'],
};

/** Strip surrounding whitespace, casing and a single trailing sentence mark. */
export function normalizeCommand(text) {
  return String(text == null ? '' : text)
    .trim()
    .toLowerCase()
    .replace(/[.,!?]$/, '')
    .trim();
}

/**
 * Classify an utterance as a navigation command.
 *
 * @param   {string} text  raw transcript
 * @returns {'accept'|'redo'|'skip'|'back'|'exit'|null}  null when it's an answer
 */
export function matchCommand(text) {
  const clean = normalizeCommand(text);
  if (!clean) return null;

  const entry = Object.keys(COMMANDS).find((name) => COMMANDS[name].includes(clean));
  return entry || null;
}

/** The full vocabulary, for rendering an on-screen hint. */
export const COMMAND_WORDS = COMMANDS;
